import { Children, cloneElement, isValidElement, memo, useMemo } from 'react';

import { useWindowWidth } from '@hooks/useWindowWidth';

import { Dialog, Drawer, TwModal as Modal, Sheet } from '@ui-component-library';

import {
  ActiveModalType,
  BreakpointConfig,
  DynamicModalProps,
  SubComponentProps
} from './DynamicModal.types';

const DEFAULT_BP: BreakpointConfig = { sm: 'drawer', lg: 'sheet' };

const useActiveModalType = (bp: BreakpointConfig) => {
  const { isSmall, isLarge } = useWindowWidth();

  return useMemo(() => {
    if (isSmall && bp.sm) {
      return bp.sm as ActiveModalType;
    }

    if (isLarge && bp.lg) {
      return bp.lg as ActiveModalType;
    }

    return (bp.sm || 'modal') as ActiveModalType;
  }, [isSmall, isLarge, bp.sm, bp.lg]);
};

// Memoized or 'memo' is used to prevent unnecessary rerenders if the props are the same
const Header = memo((props: SubComponentProps) => {
  const activeModalType = useActiveModalType(props.bp || DEFAULT_BP);

  if (activeModalType === 'drawer') {
    return <Drawer.Header {...props} />;
  }
  if (activeModalType === 'sheet') {
    return <Sheet.Header {...props} />;
  }
  if (activeModalType === 'dialog') {
    return <Dialog.Header {...props} />;
  }

  return <Modal.Header {...props} />;
});

// Memoized or 'memo' is used to prevent unnecessary rerenders if the props are the same
const Content = memo((props: SubComponentProps) => {
  const activeModalType = useActiveModalType(props.bp || DEFAULT_BP);

  if (activeModalType === 'drawer') {
    return <Drawer.Content {...props} />;
  }
  if (activeModalType === 'sheet') {
    return <Sheet.Content {...props} />;
  }
  if (activeModalType === 'dialog') {
    return <Dialog.Content {...props} />;
  }
  return <Modal.Content {...props} />;
});

// Memoized or 'memo' is used to prevent unnecessary rerenders if the props are the same
const Footer = memo((props: SubComponentProps) => {
  const activeModalType = useActiveModalType(props.bp || DEFAULT_BP);

  if (activeModalType === 'drawer') {
    return <Drawer.Footer {...props} />;
  }
  if (activeModalType === 'sheet') {
    return <Sheet.Footer {...props} />;
  }
  if (activeModalType === 'dialog') {
    return <Dialog.Footer {...props} />;
  }
  return <Modal.Footer {...props} />;
});

export const DynamicModal = ({
  bp = DEFAULT_BP,
  children,
  isOpen,
  onClose,
  className,
  dialogSize = 'small',
  drawerSize = 'content'
}: DynamicModalProps) => {
  const activeModalType = useActiveModalType(bp);

  const childrenWithProps = useMemo(() => {
    if (!isOpen) {
      return null;
    }
    return Children.map(children, (child: React.ReactNode) => {
      if (isValidElement(child)) {
        return cloneElement(child, {
          ...(child.props as object),
          bp
        } as SubComponentProps);
      }
      return child;
    });
  }, [children, bp, isOpen]);

  // Memoize wrapper component selection to prevent unnecessary rerenders. Will only rerender if the activeModalType changes.
  const wrapperComponent = useMemo(() => {
    if (!isOpen) {
      return null;
    }

    if (activeModalType === 'drawer') {
      return (
        <Drawer isOpen={isOpen} onClose={onClose} size={drawerSize} className={className}>
          {childrenWithProps}
        </Drawer>
      );
    }
    if (activeModalType === 'sheet') {
      return (
        <Sheet isOpen={isOpen} onClose={onClose} side={'right'} className={className}>
          {childrenWithProps}
        </Sheet>
      );
    }
    if (activeModalType === 'dialog') {
      return (
        <Dialog isOpen={isOpen} size={dialogSize} className={className}>
          {childrenWithProps}
        </Dialog>
      );
    }
    return (
      <Modal isOpen={isOpen} className={className}>
        {childrenWithProps}
      </Modal>
    );
  }, [activeModalType, isOpen, onClose, className, childrenWithProps, dialogSize, drawerSize]);

  if (!isOpen) {
    return null;
  }

  return wrapperComponent;
};

DynamicModal.Header = Header;
DynamicModal.Content = Content;
DynamicModal.Footer = Footer;
